/* @flow */
/* global fsgBuild */

import React from 'react';
import { Provider } from 'react-redux';
import { Router } from 'react-router';
import { BrowserRouter } from 'react-router-dom';
import { get } from 'lodash';
import * as Sentry from '@sentry/react';

import { isLoggedIn } from 'app/api/auth';
import usePortalCustomEvent, { portalCustomEvents } from 'app/hooks/usePortalCustomEvent';
import { userSignOut, portalSignIn } from 'app/actions/userActions';
import config from './config';
import PxcProvider from './providers/PxcProvider';
import HistoryListener from './providers/HistoryListener';
import { getBanners } from './actions/settingsActions';
import Loader from './components/commons/Loader';
import App from './containers/App';
import ScrollToTop from './components/util/ScrollToTop';
import { UNAUTHORISED_DS_API } from './constants/customEvents';
import { FANTASY_CLIENT_ID } from './constants/portalClient';
import useWebViewAuthentication from './hooks/useWebViewAuthentication';
import AppRouter from './components/AppRouter';

import './ionicons.min.css';
import './barlow.css';
import './less/base/loader.less';
import './less/site.less';

import(`../config/brands/${process.env.APP_BRAND}.scss`);

if (config.settings.sentry.dsn) {
  Sentry.init({
    dsn: config.settings.sentry.dsn,
    enabled: process.env.APP_ENV === 'production'
    // release: config.settings.sentryRelease, Releases autoinjected via webpack plugin - can change if needed
  });
}

// type Props = {
//   history: Object,
//   store: Object
// };

const havePortal = !!get(window, 'portal.client.getAuthToken');

function Root(props) {
  const { history, store } = props;

  const mountOnFirstLoad = window.location.pathname.startsWith('/fantasy') || !havePortal;
  const [appMounted, setAppMounted] = React.useState(mountOnFirstLoad);
  useWebViewAuthentication(store.dispatch);

  const [isPortalLoggedIn, setIsPortalLoggedIn] = React.useState(
    havePortal ? window.portal.user.isLoggedIn() : false
  );
  const isAuthenticatingRef = React.useRef(false);

  React.useEffect(() => {
    store.dispatch(getBanners());
  }, []);

  usePortalCustomEvent(
    portalCustomEvents.FANTASY_APP_MOUNT,
    React.useCallback(eventDetail => {
      setAppMounted(eventDetail ? eventDetail.mounted : false);
    }, [])
  );

  usePortalCustomEvent(
    portalCustomEvents.EVENT_PORTAL_LOGGED_IN,
    React.useCallback(() => {
      setIsPortalLoggedIn(true);
    }, [])
  );

  usePortalCustomEvent(
    portalCustomEvents.EVENT_PORTAL_LOGGED_OUT,
    React.useCallback(() => {
      setIsPortalLoggedIn(false);
    }, [])
  );

  const authenticateFantasy = React.useCallback(() => {
    // if is already authenticating or there is no portal don't run this effect
    if (isAuthenticatingRef.current || !havePortal) return;

    // if there is a token for portal but not for DS
    if (isPortalLoggedIn && !isLoggedIn()) {
      isAuthenticatingRef.current = true;

      window.portal.client
        .getAuthToken(FANTASY_CLIENT_ID)
        .then(authCode => {
          store.dispatch(portalSignIn(authCode));
        })
        .catch(() => {})
        .finally(() => {
          isAuthenticatingRef.current = false;
        });
    }
    // if there is no token for portal but there is still one present for DS
    else if (!isPortalLoggedIn && isLoggedIn()) {
      store.dispatch(userSignOut());
    }
  }, [isPortalLoggedIn, store]);

  React.useEffect(authenticateFantasy, [authenticateFantasy]);

  // set up listener for api 401 unauthorised
  React.useEffect(() => {
    document.body.addEventListener(UNAUTHORISED_DS_API, authenticateFantasy);

    return () => {
      document.body.removeEventListener(UNAUTHORISED_DS_API, authenticateFantasy);
    };
  }, [authenticateFantasy]);

  return appMounted ? (
    <Provider store={store}>
      <PxcProvider>
        <Router history={history}>
          <BrowserRouter basename="/fantasy">
            <HistoryListener>
              <ScrollToTop>
                <App>
                  <React.Suspense fallback={<Loader />}>
                    <AppRouter />
                  </React.Suspense>
                </App>
              </ScrollToTop>
            </HistoryListener>
          </BrowserRouter>
        </Router>
      </PxcProvider>
    </Provider>
  ) : null;
}

export default Root;
